// Authentication utilities - Centralized token management
// This ensures all components use the same token source

/**
 * Safely decode JWT payload without throwing (avoids InvalidCharacterError from atob).
 * Returns payload object or null if token is missing, malformed, or not base64.
 */
export const safeDecodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) base64 += '===='.slice(0, 4 - pad);
    const decoded = atob(base64);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

/**
 * Get authentication token from localStorage
 * Priority order: adminToken > fitconnect_token > authToken > token
 */
export const getAuthToken = () => {
  const adminToken = localStorage.getItem('adminToken');
  const fitconnectToken = localStorage.getItem('fitconnect_token');
  const authToken = localStorage.getItem('authToken');
  const token = localStorage.getItem('token');

  return adminToken || fitconnectToken || authToken || token;
};

/**
 * Get admin token specifically
 */
export const getAdminToken = () => {
  return localStorage.getItem('adminToken');
};

/**
 * Clear all authentication tokens
 */
export const clearAllTokens = () => {
  localStorage.removeItem('fitconnect_token');
  localStorage.removeItem('authToken');
  localStorage.removeItem('token');
  localStorage.removeItem('adminToken');
  localStorage.removeItem('userData');
  localStorage.removeItem('user');
  localStorage.removeItem('adminUserData');
  localStorage.removeItem('adminRefreshToken');
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  const token = getAuthToken();
  return !!token;
};

/**
 * Get user data from localStorage
 */
export const getUserData = () => {
  const userData = localStorage.getItem('userData');
  const user = localStorage.getItem('user');
  const adminUserData = localStorage.getItem('adminUserData');
  
  if (userData) {
    try {
      return JSON.parse(userData);
    } catch (e) {
      console.error('Error parsing userData:', e);
    }
  }
  
  if (user) {
    try {
      return JSON.parse(user);
    } catch (e) {
      console.error('Error parsing user:', e);
    }
  }
  
  if (adminUserData) {
    try {
      return JSON.parse(adminUserData);
    } catch (e) {
      console.error('Error parsing adminUserData:', e);
    }
  }
  
  return null;
};

/**
 * True when JWT has exp claim and it is in the past.
 * Opaque / non-JWT tokens return false (not treated as expired client-side).
 */
export const isJwtExpired = (token) => {
  const payload = safeDecodeJwtPayload(token);
  if (!payload?.exp) return false;
  return payload.exp < Math.floor(Date.now() / 1000);
};

/**
 * Remove all auth-related keys used across roles (client, trainer, admin).
 */
export const clearAuthSessionStorage = () => {
  const keys = [
    'authUser',
    'authToken',
    'refreshToken',
    'fitconnect_token',
    'fitconnect_user',
    'fitconnect_session',
    'userId',
    'user',
    'token',
    'userData',
    'adminToken',
    'adminRefreshToken',
    'adminUserData',
    'adminProfile',
    'userProfile',
    'lastActivity',
    'sessionData',
  ];
  keys.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

export const clearAdminSessionStorage = () => {
  ['adminToken', 'adminRefreshToken', 'adminUserData', 'adminProfile'].forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

/** Notify SimpleAuth and route guards that storage auth was cleared (same tab). */
export const notifyAuthSessionChanged = (reason = 'session_cleared') => {
  try {
    window.dispatchEvent(new CustomEvent('authLogout', { detail: { reason } }));
    window.dispatchEvent(new CustomEvent('authUpdate', { detail: { reason } }));
  } catch {
    // ignore
  }
};

/**
 * Read user + token from storage if session is still valid (JWT not expired).
 */
export const getValidStoredSession = () => {
  const adminToken = localStorage.getItem('adminToken');
  const adminUserData = localStorage.getItem('adminUserData');
  if (adminToken && adminUserData) {
    if (isJwtExpired(adminToken)) {
      clearAdminSessionStorage();
      return null;
    }
    try {
      return { user: JSON.parse(adminUserData), token: adminToken, isAdmin: true };
    } catch {
      clearAdminSessionStorage();
      return null;
    }
  }

  const token =
    localStorage.getItem('authToken') ||
    localStorage.getItem('fitconnect_token') ||
    localStorage.getItem('token');
  const savedUser =
    localStorage.getItem('authUser') ||
    sessionStorage.getItem('authUser') ||
    localStorage.getItem('fitconnect_user') ||
    sessionStorage.getItem('fitconnect_user') ||
    localStorage.getItem('user');

  if (!token || !savedUser) return null;

  if (isJwtExpired(token)) {
    clearAuthSessionStorage();
    return null;
  }

  try {
    return { user: JSON.parse(savedUser), token, isAdmin: false };
  } catch {
    clearAuthSessionStorage();
    return null;
  }
};

/** Whether localStorage still has a non-expired session (source of truth for guest routes). */
export const hasValidSessionInStorage = () => !!getValidStoredSession();
